import { FC, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { reconciliationApi } from '../../../api/reconciliationApi';
import { useToast } from '../../ui/Toast';
import { cn } from '../../../utils/cn';

interface ResolveDiscrepancyModalProps {
  discrepancyId: string;
  onClose: () => void;
  onResolved: () => void;
}

type ResolutionAction = 'APPROVED' | 'ADJUSTED' | 'REFUNDED' | 'IGNORED';

const resolutionOptions: { value: ResolutionAction; label: string; description: string }[] = [
  {
    value: 'APPROVED',
    label: 'Approved',
    description: 'The amounts are correct as recorded',
  },
  {
    value: 'ADJUSTED',
    label: 'Adjusted',
    description: 'Manual adjustment has been applied',
  },
  {
    value: 'REFUNDED',
    label: 'Refunded',
    description: 'Payment has been refunded',
  },
  {
    value: 'IGNORED',
    label: 'Ignored',
    description: 'Not actionable or requires no action',
  },
];

export const ResolveDiscrepancyModal: FC<ResolveDiscrepancyModalProps> = ({
  discrepancyId,
  onClose,
  onResolved,
}) => {
  const { showToast } = useToast();
  const [action, setAction] = useState<ResolutionAction>('APPROVED');
  const [notes, setNotes] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      reconciliationApi.resolveDiscrepancy(discrepancyId, action, notes || undefined),
    onSuccess: () => {
      showToast({
        type: 'success',
        title: 'Discrepancy Resolved',
        message: 'The discrepancy has been successfully resolved.',
      });
      onResolved();
    },
    onError: (error: Error & { response?: { data?: { error?: string } } }) => {
      showToast({
        type: 'error',
        title: 'Resolution Failed',
        message: error.response?.data?.error || 'Failed to resolve discrepancy',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white dark:bg-slate-800 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-slate-700 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
            Resolve Discrepancy
          </h3>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 px-6 py-4">
            {/* Resolution Action */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-slate-300">
                Resolution Action
              </label>
              <div className="space-y-2">
                {resolutionOptions.map((option) => (
                  <label
                    key={option.value}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                      action === option.value
                        ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20'
                        : 'border-gray-200 hover:border-gray-300 dark:border-slate-600 dark:hover:border-slate-500'
                    )}
                  >
                    <input
                      type="radio"
                      name="resolutionAction"
                      value={option.value}
                      checked={action === option.value}
                      onChange={(e) => setAction(e.target.value as ResolutionAction)}
                      className="mt-1"
                    />
                    <div>
                      <span className="block font-medium text-gray-900 dark:text-slate-100">
                        {option.label}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-slate-400">
                        {option.description}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label
                htmlFor="resolution-notes"
                className="mb-2 block text-sm font-medium text-gray-700 dark:text-slate-300"
              >
                Notes (optional)
              </label>
              <textarea
                id="resolution-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="h-24 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                placeholder="Add notes about this resolution..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t border-gray-200 dark:border-slate-700 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mutation.isPending ? 'Resolving...' : 'Resolve'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
